import { Button, ButtonProps, Spinner, Text } from "@ttc3k/trekker";
import { Box, Center } from "styled-system/jsx";
import { ArrowRight } from "iconoir-react";
import { SwiperSlide } from "swiper/react";
import { EventManyQuery } from "gql/generated";
import { ArrowLeft } from "iconoir-react";
import { Flex } from "styled-system/jsx";
import { useSwiperScroll } from "shared/hooks";
import { Swiper } from "swiper/react";
import { EventWidgetCard, Skeleton } from "shared/components";
import { Scrollbar } from "swiper/modules";
import { useEventsWidgetContext } from "../EventsContext";
import { EventWidgetSize } from "widgets/events/types";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { PostHogCustomEvent } from "shared/hooks/usePostHog";

/**
 * Used to render the "More Events" button
 * @param param0
 * @returns
 */
const RenderMoreEventsButton = ({
  moreEventsLink,
  ctaProps,
}: {
  moreEventsLink: string;
  ctaProps?: ButtonProps;
}) => {
  return (
    <a href={moreEventsLink} style={{ pointerEvents: "auto" }}>
      <Button
        part="widget__btn-more-events"
        pointerEvents={"auto"}
        type="button"
        {...ctaProps}
      />
    </a>
  );
};

type EventsCarouselProps = {
  events: EventManyQuery["eventMany"];
  loading: boolean;
  carouselNoEventsText?: string;
};

export const EventsCarousel = ({
  events,
  loading,
  carouselNoEventsText,
}: EventsCarouselProps) => {
  const { t } = useTranslation("events", { keyPrefix: "WIDGET" });

  const { hideCarouselScrollbar, moreEventsLink, widgetSize } =
    useEventsWidgetContext();

  const { scroll, setSwiper, handleNextClick, handlePrevClick } =
    useSwiperScroll();

  const isSm = widgetSize === EventWidgetSize.Mobile;
  const isMd = widgetSize === EventWidgetSize.Tablet;
  const isLg = widgetSize === EventWidgetSize.Desktop;

  const horizontalClass = useMemo(() => {
    return hideCarouselScrollbar
      ? "swiper-scrollbar-horizontal-hidden"
      : moreEventsLink
        ? isSm
          ? "swiper-scrollbar-horizontal-small"
          : "swiper-scrollbar-horizontal-left"
        : isSm
          ? "swiper-scrollbar-horizontal-small"
          : "swiper-scrollbar-horizontal-right";
  }, [hideCarouselScrollbar, moreEventsLink, isSm]);

  // This is a workaround to prevent flashing as Swiper is initilized, but not fully drawn
  const [swiperFullyLoaded, setSwiperFullyLoaded] = useState(false);
  const handleSwiperAfterInit = () => {
    setTimeout(() => setSwiperFullyLoaded(true), 100);
  };

  if (loading) {
    return (
      <Center h="360px" w="full">
        <Spinner w="32px" h="32px" />
      </Center>
    );
  }

  if (events.length === 0) {
    return (
      <Skeleton
        part="carousel__container-no-events"
        borderRadius="100"
        bgColor="bg.wa.lightest"
        h="293px"
        w="full"
        p="800"
        display="flex"
        flexDir="column"
        justifyContent="center"
        alignItems="center"
        gap="300"
      >
        <Text
          part="carousel__no-events-text"
          visual="bodyMedium"
          color="text.mid"
          textAlign="center"
          maxW="550px"
        >
          {carouselNoEventsText ?? t("NO_DATA.CAROUSEL")}
        </Text>
        {moreEventsLink &&
          RenderMoreEventsButton({
            moreEventsLink,
            ctaProps: {
              visual: "accent.blue",
              mode: "dark",
              children: t("BUTTONS.MORE_EVENTS"),
            },
          })}
      </Skeleton>
    );
  }

  return (
    <Flex flexDir={"column"} width={"full"} position={"relative"}>
      <Box
        position={"relative"}
        overflowX={"hidden"}
        part={"carousel__container"}
      >
        <Swiper
          key={horizontalClass}
          scrollbar={{
            hide: false,
            draggable: true,
            snapOnRelease: true,
            horizontalClass,
          }}
          modules={[Scrollbar]}
          slidesPerView={"auto"}
          freeMode={true}
          spaceBetween={isSm ? "16px" : isMd ? "24px" : isLg ? "32px" : "16px"}
          onInit={setSwiper}
          onAfterInit={handleSwiperAfterInit}
          height={360}
        >
          {events.map(event => (
            <SwiperSlide key={event._id} style={{ width: "auto" }}>
              <EventWidgetCard event={event} orientation="portrait" />
            </SwiperSlide>
          ))}
        </Swiper>
        <Box
          position={"absolute"}
          h={"full"}
          w={{ base: "100px", sm: "128px" }}
          right={0}
          top={0}
          bg={
            "linear-gradient(90deg, rgba(255, 253, 251, 0.00) 0%, #FFFDFB 83.98%)"
          }
          pointerEvents={"none"}
          display={scroll.isEnd || !scroll.isScrollOpen ? "none" : "block"}
          zIndex={"10"}
          part={"carousel__gradient"}
        />
      </Box>
      <Flex
        part={"widget__controls"}
        gap={"150"}
        position={"absolute"}
        bottom={"0"}
        left={"0"}
        right={"0"}
        flexDir={"row"}
        justifyContent={!isSm && moreEventsLink ? "space-between" : "flex-end"}
        alignItems={"center"}
        p="1px"
        zIndex={"20"}
        pointerEvents={"none"}
      >
        {scroll.isScrollOpen && !isSm && swiperFullyLoaded ? (
          <Flex flexDir={"row"} gap={"150"} pointerEvents={"auto"}>
            <Button
              Icon={ArrowLeft}
              visual="primary"
              mode={"light"}
              onClick={() =>
                handlePrevClick(PostHogCustomEvent.ScrollEventsLeft)
              }
              part={"widget__btn-prev"}
              disabled={scroll.isBeginning}
            />
            <Button
              Icon={ArrowRight}
              visual="primary"
              mode={"light"}
              onClick={() =>
                handleNextClick(PostHogCustomEvent.ScrollEventsRight)
              }
              part={"widget__btn-next"}
              disabled={scroll.isEnd}
            />
          </Flex>
        ) : (
          // Ensures the "More Events" button will always be on the right when there is NO scrolling
          <div></div>
        )}

        {moreEventsLink &&
          swiperFullyLoaded &&
          RenderMoreEventsButton({
            moreEventsLink,
            ctaProps: {
              visual: "primary",
              mode: "light",
              children: t("BUTTONS.MORE_EVENTS"),
            },
          })}
      </Flex>
    </Flex>
  );
};
